<template>
  <div>
    <Modal
      ref="modal"
      :title="currentVariation.title"
      :action="currentVariation.actionTitle"
      action-color="indigo"
      @close-modal="handleCancel"
      @handle-action="handleAction"
    >
      <div v-if="variation === 'task-selection'">
        <RadioSelect v-model="selectedChecklist" class="mt-4" :options="checklistOptions" />
      </div>

      <div v-if="variation === 'loading'" class="spinner"></div>

      <div v-if="variation === 'no-tasks'">
        <ZeroState>
          <p>
            Humaniz can help you automate Onboarding, Training, and Offboarding. Go to settings to set up your first
            Onboarding task checklist.
          </p></ZeroState
        >
      </div>

      <div v-if="variation === 'task-preview'" class="grid grid-cols-1 gap-2">
        <div v-if="selectedTaskChecklist?.checklist_tasks.length || 0 > 0">
          <div
            v-for="task in selectedTaskChecklist?.checklist_tasks"
            :key="task.id"
            class="p-4 bg-gray-100 rounded-md mb-2"
          >
            <p>{{ task.name }}</p>
          </div>
        </div>
        <div v-if="selectedTaskChecklist?.checklist_tasks.length === 0">
          <ZeroState>
            <div class="flex justify-center gap-8">
              <header>This checklist has no tasks setup.</header>
            </div>
          </ZeroState>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { Api } from '@/api';
import Modal from '../shared/Modal.vue';
import RadioSelect from '../shared/RadioSelect.vue';
import ZeroState from '../ZeroState.vue';
import { computed, onMounted, ref, type ComponentPublicInstance } from 'vue';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import type { TaskChecklist } from '@/api/task_checklists';
import { useRouter } from 'vue-router';
import { toast } from 'vue-sonner';

type VariationId = 'task-selection' | 'task-preview' | 'no-tasks' | 'loading';
type VariationDetails = {
  title: string;
  actionTitle: string;
  onAction: () => void;
};
type ContactAssignTasksProps = {
  record: { id: number };
};

const { record } = defineProps<ContactAssignTasksProps>();
const loadingSpinnerStore = useLoadingSpinnerStore();
const router = useRouter();
const emit = defineEmits(['handleCancel', 'handleSubmit']);

const modal = ref<ComponentPublicInstance<typeof Modal> | null>(null);
const taskChecklists = ref<TaskChecklist[]>([]);
const selectedChecklist = ref<string | null>(null);
const variation = ref<VariationId>('loading');

const currentVariation = computed(() => {
  const loading: VariationDetails = {
    title: '',
    actionTitle: '',
    onAction: () => {},
  };

  const taskSelection: VariationDetails = {
    title: 'What tasks should we assign?',
    actionTitle: 'Preview',
    onAction: () => (variation.value = 'task-preview'),
  };

  const taskPreview: VariationDetails = {
    title: 'Preview Tasks',
    actionTitle: 'Assign',
    onAction: async () => await assignTasks(),
  };

  const noTasks: VariationDetails = {
    title: "You don't have any task checklists enabled",
    actionTitle: 'Go To Settings',
    onAction: () => router.push('/settings/company/onboarding_checklists'),
  };

  const variations = new Map<VariationId, VariationDetails>([
    ['task-selection', taskSelection],
    ['task-preview', taskPreview],
    ['no-tasks', noTasks],
    ['loading', loading],
  ]);

  return variations.get(variation.value)!;
});

const checklistOptions = computed(() => {
  return taskChecklists.value.map(checklist => ({
    label: checklist.name,
    description: '',
  }));
});

const selectedTaskChecklist = computed(() =>
  taskChecklists.value.find(checklist => checklist.name === selectedChecklist.value)
);

const handleAction = async () => {
  loadingSpinnerStore.showLocalLoadingSpinner();
  currentVariation.value.onAction();
  loadingSpinnerStore.hideLocalLoadingSpinner();
};

const handleCancel = () => {
  emit('handleCancel');
};

const getTaskChecklists = async () => {
  const { records } = await Api.TaskChecklists.listTaskChecklists({ status: 'enabled' });
  if (records.length > 0) {
    taskChecklists.value = records;
    selectedChecklist.value = records[0].name;
    variation.value = 'task-selection';
  } else {
    variation.value = 'no-tasks';
  }
};

const assignTasks = async () => {
  const data = await Api.TaskChecklistAssignment.createTaskChecklistAssignment({
    taskChecklistId: selectedTaskChecklist.value?.id || -1,
    contactId: record.id,
  });

  if (data.status !== 'error') {
    emit('handleSubmit');

    modal.value?.success();
    toast.success('Tasks assigned', {
      action: { label: 'Dismiss' },
      description: 'The tasks have been successfully assigned. Use the button below to dismiss this alert.',
    });
  } else {
    modal.value?.error();
    toast.error('Task assignment error', {
      action: { label: 'Dismiss' },
      description: data.message || '',
    });
  }
};

onMounted(async () => {
  await getTaskChecklists();
  loadingSpinnerStore.hideLocalLoadingSpinner();
});
</script>
